import React, { memo, useMemo } from 'react';

import { getCacheKey, getPolygonBounds } from '~/helpers/supply';

import AnimatedTileLayer from './AnimatedTileLayer';
import styles from './PreviewLayer.module.scss';

import type { BUILDINGS } from '~/Actions/ActionSupply';
import type { ISupplyBuildings } from '~/store/slices/settingsSlice';

type IProps = {
  buildings: ISupplyBuildings;
  tempHoverBuildingId?: Nullable<BUILDINGS>;
  tempHoverBuildingLevel?: Nullable<{ level: number }>;
  confirmBuildingId?: Nullable<BUILDINGS>;
};

const PreviewLayer: React.FC<IProps> = ({
  buildings,
  tempHoverBuildingId,
  tempHoverBuildingLevel,
  confirmBuildingId,
}) => {
  const { previewBuilding, level, isBuilt, isConfirmingLevel } = useMemo(() => {
    if (tempHoverBuildingLevel && tempHoverBuildingId && buildings[tempHoverBuildingId]) {
      const building = buildings[tempHoverBuildingId];
      const lvl = tempHoverBuildingLevel.level;
      const built = lvl <= building.level;
      const confirmingLevel = confirmBuildingId === building.name && lvl === building.level + 1;

      return { previewBuilding: building, level: lvl, isBuilt: built, isConfirmingLevel: confirmingLevel };
    }

    if (confirmBuildingId && buildings[confirmBuildingId]) {
      const building = buildings[confirmBuildingId];
      const lvl = Math.min(building.level + 1, building.maxLevel);
      const confirmingLevel = confirmBuildingId === building.name;

      return { previewBuilding: building, level: lvl, isBuilt: false, isConfirmingLevel: confirmingLevel };
    }

    return { previewBuilding: null, level: null, isBuilt: false, isConfirmingLevel: false };
  }, [buildings, tempHoverBuildingId, tempHoverBuildingLevel, confirmBuildingId]);

  // Memoize the geo data and bounds
  const { geo, bounds } = useMemo(() => {
    if (previewBuilding && level !== null) {
      const levelData = previewBuilding.levels[level];
      const geoData = levelData?.geo?.edges;

      if (geoData) {
        const cacheKey = getCacheKey(previewBuilding.name, level);
        return { geo: geoData, bounds: getPolygonBounds(cacheKey, geoData) };
      }
    }

    return { geo: null, bounds: null };
  }, [previewBuilding, level]);

  if (previewBuilding && geo && bounds) {
    const tileLayerClassName = isBuilt || isConfirmingLevel ? styles.previewLayer : styles.previewLayerFuture;
    const tileLayerKey = `Preview_${previewBuilding.name}_${isConfirmingLevel}_${level}`;

    return (
      <AnimatedTileLayer
        key={tileLayerKey}
        className={tileLayerClassName}
        url={previewBuilding.levels[level].url}
        bounds={bounds}
        zIndex={1}
      />
    );
  }

  return null;
};

export default memo(PreviewLayer);
